import { parseAskingPrice } from '@features/buyer/hire/job-price-calculator/priceCalculator';
import { useDebouncedValue } from '@features/buyer/jobs/useDebouncedValue';
import { apiErrorMessage } from '@lib/api/apiError';
import { fetchBuyerCost } from '@lib/api/costs';
import { queryKeys } from '@lib/api/queryKeys';
import { useQuery } from '@tanstack/react-query';
import i18n from '@translations/i18n';

const BUYER_COST_DEBOUNCE_MS = 300;

type UseBuyerCostEstimateArgs = {
  categoryId?: string | number | null;
  askingPrice: string;
};

export const useBuyerCostEstimate = ({
  categoryId,
  askingPrice,
}: UseBuyerCostEstimateArgs) => {
  const debouncedAskingPrice = useDebouncedValue(
    askingPrice,
    BUYER_COST_DEBOUNCE_MS
  );
  const parsed = parseAskingPrice(debouncedAskingPrice);
  const validPrice = parsed.status === 'valid' ? parsed.price : null;
  const enabled = Boolean(categoryId) && validPrice != null;

  const query = useQuery({
    queryKey: queryKeys.buyerCost(categoryId, validPrice),
    queryFn: () => fetchBuyerCost(categoryId as string | number, validPrice!),
    enabled,
    staleTime: 60_000,
  });

  const buyerCostError: string =
    parsed.status === 'invalid'
      ? i18n.t('hire.invalidAskingPrice')
      : query.isError
        ? (apiErrorMessage(query.error) ??
          i18n.t('hire.unableToCalculateFeeRetry'))
        : '';

  const isBuyerCostLoading =
    enabled &&
    (debouncedAskingPrice !== askingPrice ||
      query.isFetching ||
      query.isPending);

  return {
    buyerCostState: enabled && !buyerCostError ? (query.data ?? null) : null,
    buyerCostError,
    isBuyerCostLoading,
  };
};
